import { Link } from "react-router-dom";
import { PackageSearch, Store, Truck, Target, ShoppingBag, MapPin, MessageSquare } from "lucide-react";
import Seo from "@/components/Seo";
import AmazonButton from "@/components/AmazonButton";
import CategoryCard from "@/components/CategoryCard";
import BrandsSection from "@/components/BrandsSection";
import GenuineProductsSection from "@/components/GenuineProductsSection";
import { CATEGORIES } from "@/lib/site";

const steps = [
  {
    icon: PackageSearch,
    title: "Product Selection",
    text: "We identify practical automotive replacement and maintenance products based on quality, compatibility, and customer demand.",
  },
  {
    icon: Store,
    title: "Amazon Marketplace",
    text: "Our products are listed and sold through the Amazon US marketplace.",
  },
  {
    icon: Truck,
    title: "Fulfillment by Amazon",
    text: "Eligible inventory is stored and fulfilled through Amazon's fulfillment network, supporting dependable delivery and customer service.",
  },
];

const reasons = [
  {
    icon: Target,
    title: "Focused Product Selection",
    text: "We work within a defined set of automotive categories rather than listing unrelated product lines.",
  },
  {
    icon: ShoppingBag,
    title: "Convenient Amazon Purchasing",
    text: "Customers order through Amazon, using an account and checkout process they already know.",
  },
  {
    icon: MapPin,
    title: "U.S. Market Focus",
    text: "Our operations are directed at customers located in the United States.",
  },
  {
    icon: MessageSquare,
    title: "Clear Customer Support",
    text: "Order questions are handled through Amazon order support, and general business inquiries by email.",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="AutoNeova | Automotive Replacement Products on Amazon"
        description="AutoNeova is a U.S.-based e-commerce company offering automotive replacement and maintenance products through Amazon."
        path="/"
      />

      {/* Hero */}
      <section data-testid="hero-section" className="relative isolate overflow-hidden bg-[#0B1F33]">
        <img
          src="/images/hero-automotive-products.webp"
          alt="Automotive wiper blades, spark plugs and filters offered by AutoNeova."
          width="2400"
          height="1000"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-[72%_center] lg:object-[85%_center]"
        />
        <div
          className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0B1F33]/97 via-[#0B1F33]/92 to-[#0B1F33]/70 lg:from-[#0B1F33]/92 lg:via-[#0B1F33]/70 lg:to-[#0B1F33]/25"
          aria-hidden="true"
        />
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-32">
          <div className="reveal max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#4E93F5]">
              U.S.-based automotive e-commerce
            </p>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.08] text-white sm:text-5xl lg:text-6xl">
              Reliable Automotive Parts, Delivered Through Amazon
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
              AutoNeova is a U.S.-based e-commerce company offering carefully selected automotive
              replacement and maintenance products to customers across the United States.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <AmazonButton testId="hero-amazon-cta" variant="primary" />
              <Link
                to="/about"
                data-testid="hero-learn-more-btn"
                className="inline-flex items-center justify-center rounded-md border-2 border-white/80 px-6 py-3 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-white/10"
              >
                Learn About AutoNeova
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section data-testid="about-section" className="bg-white py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1F6FEB]">
              About AutoNeova
            </p>
            <h2 className="mt-4 text-3xl font-extrabold text-[#0B1F33] md:text-4xl">
              An independent automotive e-commerce business
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-[#263238] md:text-lg">
              AutoNeova is an independent automotive e-commerce business focused on making reliable
              replacement and maintenance products accessible to customers throughout the United
              States. Our products are offered through Amazon, providing customers with a familiar
              and convenient purchasing experience.
            </p>
            <Link
              to="/about"
              data-testid="about-section-link"
              className="mt-8 inline-flex items-center rounded-md border-2 border-[#0B1F33] px-6 py-3 text-sm font-semibold text-[#0B1F33] transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#F4F6F8]"
            >
              Read more about us
            </Link>
          </div>
        </div>
      </section>

      {/* How we operate */}
      <section data-testid="how-we-operate-section" className="bg-[#F4F6F8] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1F6FEB]">
            How We Operate
          </p>
          <h2 className="mt-4 max-w-2xl text-3xl font-extrabold text-[#0B1F33] md:text-4xl">
            A straightforward marketplace model
          </h2>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {steps.map((s, i) => (
              <div
                key={s.title}
                data-testid={`operate-step-${i + 1}`}
                className="rounded-lg border border-[#0B1F33]/10 bg-white p-8"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#1F6FEB]/10 text-[#1F6FEB]">
                    <s.icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-[Manrope] text-3xl font-extrabold text-[#0B1F33]/10">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="mt-6 text-lg font-bold text-[#0B1F33]">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#263238]">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-sm text-[#263238]/80">
            AutoNeova sells as an independent third-party seller on the Amazon US marketplace.
            Amazon does not own, sponsor, endorse, or formally affiliate with AutoNeova.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section data-testid="categories-section" className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1F6FEB]">
                Product Categories
              </p>
              <h2 className="mt-4 text-3xl font-extrabold text-[#0B1F33] md:text-4xl">
                What we offer
              </h2>
            </div>
            <Link
              to="/products"
              data-testid="categories-products-link"
              className="text-sm font-semibold text-[#1F6FEB] underline decoration-[#1F6FEB]/30 underline-offset-4 transition-colors hover:decoration-[#1F6FEB]"
            >
              View all categories
            </Link>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      <BrandsSection background="light" />
      <GenuineProductsSection background="white" />

      {/* Why AutoNeova */}
      <section data-testid="why-section" className="bg-[#F4F6F8] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1F6FEB]">
            Why AutoNeova
          </p>
          <h2 className="mt-4 max-w-2xl text-3xl font-extrabold text-[#0B1F33] md:text-4xl">
            How we work with customers
          </h2>
          <div className="mt-14 grid gap-x-12 gap-y-10 sm:grid-cols-2">
            {reasons.map((r) => (
              <div
                key={r.title}
                data-testid={`why-item-${r.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="flex gap-5 border-t border-[#0B1F33]/10 pt-6"
              >
                <span className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#1F6FEB] ring-1 ring-[#0B1F33]/10">
                  <r.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-lg font-bold text-[#0B1F33]">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#263238]">{r.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amazon CTA */}
      <section data-testid="amazon-cta-section" className="relative isolate overflow-hidden bg-[#0B1F33]">
        <img
          src="https://images.unsplash.com/photo-1587293852726-70cdb56c2866?crop=entropy&cs=srgb&fm=jpg&w=1600&q=70&ixlib=rb-4.1.0"
          alt="Packaged shipments stacked in a distribution warehouse"
          width="1600"
          height="800"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-[#0B1F33]/88" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-extrabold text-white md:text-4xl">
              See the products currently available
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/85">
              Current availability, pricing, compatibility details, and delivery options are shown on
              our Amazon listings.
            </p>
            <AmazonButton testId="footer-cta-amazon-btn" variant="onDark" className="mt-9">
              View AutoNeova on Amazon
            </AmazonButton>
          </div>
        </div>
      </section>
    </>
  );
}
